<template>
  <MainLayout>
    <ContentPageHeader functionName="店舗移動管理" pageName="店舗移動登録(出荷)">
    </ContentPageHeader>
    
    <!-- 基本セクション -->
    <div class="search-section">
      <div class="search-form">        
        <FormDisplayField 
          v-if="storeTransfer"
          label="移動先店舗"
          :value="getStoreName(storeTransfer.destinationStore)"
        />
        
        <FormDisplayField 
          v-if="storeTransfer"
          label="移動金額"
          :value="calculateTotalAmount"
        />
        
        <FormTextField 
          label="商品コード" 
          id="productCode" 
          v-model="productCode"
          placeholder="商品コードを入力してください"
          size="medium"
        />
      </div>
    </div>

    <!-- 検索結果テーブル -->
    <div class="search-result">
      <FormListTable
        :items="displayProductList"
        :columns="columns"
        :actions="actions"
        :per-page="perPage"
        :page="currentPage"
        :totalItems="totalCount"
        :emptyMessage="'商品が登録されていません'"
        @page-change="onPageChange"
        @action="handleTableAction"
      >
      </FormListTable>
    </div>

    <template #footer-buttons>
      <div class="footer-buttons">
        <button class="btn btn-danger" @click="confirmDeleteTransfer">削除</button>
        <button class="btn btn-primary" @click="saveShipment">登録</button>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormListTable from '@/components/form/FormListTable.vue'
import FormDisplayField from '@/components/form/FormDisplayField.vue'
import service from '../../services/service';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';
import constants from '../../constants/constants.json';

export default {
  name: 'StoreTransferShippingView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormTextField,
    FormListTable,
    FormDisplayField,
  },
  data() {
    return {
      storeTransfer: null,
      productCode: '',
      allProducts: [],
      displayProductList: [],
      storeOptions: [],
      // ページング
      currentPage: 0,
      perPage: 50,
      totalCount: 0,
      // テーブルカラム定義
      columns: [
        { key: 'categoryName', label: 'カテゴリ', sortable: false, minWidth: 180, maxWidth: 400 },
        { key: 'makerName', label: 'メーカー', sortable: false, minWidth: 90, maxWidth: 450 },
        { key: 'productCode', label: '商品コード', sortable: false, minWidth: 140, maxWidth: 180 },
        { key: 'productName', label: '商品名', sortable: false, minWidth: 100, maxWidth: 600 },
        { key: 'stock', label: '出荷個数', sortable: false, minWidth: 130, maxWidth: 140 },
      ],
      actions: [
        {
          label: '削除',
          buttonClass: 'btn-danger',
          action: (item) => this.deleteProduct(item)
        }
      ],
      // カテゴリオプション
      categoryOptions: [],
      // メーカーオプション
      manufacturerOptions: [],
      // カテゴリの元データ（ランク連動用）
      originalCategories: [],
      // 公開ステータスオプション
      publicationStatusOptions: [],
    };
  },
  async mounted() {
    const navigationFlag = sessionStorage.getItem('legitimateNavigation');
    if (navigationFlag !== 'StoreTransferShipping') {
      this.$router.push({ name: 'StoreTransferList' });
      return;
    }
    sessionStorage.removeItem('legitimateNavigation');

    // 初期データ取得
    await this.getInitData();
    // URLパラメータからIDを取得
    this.loadStoreTransferData();
  },
  computed: {
    // 移動金額の計算（買取価格×在庫数の合計）
    calculateTotalAmount() {
      if (!this.allProducts || this.allProducts.length === 0) {
        return '¥0';
      }
      
      const total = this.allProducts.reduce((sum, product) => {
        const purchasePrice = product.purchasePrice || 0;
        const quantity = product.stock || 0;
        return sum + (purchasePrice * quantity);
      }, 0);
      
      return `¥${total.toLocaleString()}`;
    }
  },
  watch: {
    'productCode'(newVal) {
      if (newVal && newVal.length === constants.PRODUCT_CODE_LENGTH) {
        this.searchProduct();
      }
    }
  },
  methods: {
    // 初期データ取得処理
    async getInitData() {
      try {
        showLoading();
        // 店舗一覧を取得
        const storeResponse = await service.getStoreList();
        this.storeOptions = storeResponse.data.stores.map(store => ({
          value: store.id,
          label: store.storeName
        }));
        
        // API呼び出しでメーカー一覧を取得
        const makerResponse = await service.getMakerList();
        this.manufacturerOptions = makerResponse.data.makers.map(maker => {
          return { value: maker.id.toString(), label: maker.maker };
        });
        
        // API呼び出しでカテゴリ一覧を取得
        const categoryResponse = await service.getCategoryList();
        this.originalCategories = categoryResponse.data.categories;
        // カテゴリオプションを整形
        this.categoryOptions = [];
        for (const parent of this.originalCategories) {
          for (const child of parent.childCategories) {
            this.categoryOptions.push({
              value: child.id.toString(),
              label: `${parent.categoryType} - ${child.category}`
            });
          }
        }

        // API呼び出しで公開ステータス一覧を取得
        const publicStateResponse = await service.getPublicStateList();
        this.publicationStatusOptions = publicStateResponse.data.publicStates.map(state => {
          return { value: state.id, label: state.publicState };
        });
      } catch (error) {
        console.error('初期情報の取得に失敗しました:', error);
        alert('初期情報の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // 店舗名を取得
    getStoreName(storeId) {
      const store = this.storeOptions.find(store => store.value === storeId);
      return store ? store.label : '';
    },
    
    // 店舗移動データを取得
    async loadStoreTransferData() {
      try {
        showLoading();
        // URLパラメータから店舗移動IDを取得
        const storeTransferId = this.$route.query.id;
        if (!storeTransferId) {
          console.error('店舗移動IDが指定されていません');
          this.$router.push({ name: 'StoreTransferList' });
          return;
        }
        
        // 店舗移動データを取得
        const response = await service.getStoreTransferById(storeTransferId);
        if (!response.data.success) {
          alert('店舗移動データの取得に失敗しました');
          return;
        }
        this.storeTransfer = response.data.storeTransfer;
      } catch (error) {
        console.error('店舗移動データの取得中にエラーが発生しました:', error);
        alert('店舗移動データの取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // 商品を検索
    async searchProduct() {
      if (!this.productCode) {
        alert('商品コードを入力してください');
        return;
      }
      
      try {
        showLoading();
        // 検索パラメータの構築
        const params = new URLSearchParams();
        params.append('productCode', this.productCode);
        
        // APIリクエスト
        const response = await service.getProductList(params);
        
        if (response.data.success && response.data.products.length > 0) {
          // 取得したデータを整形して表示用に変換
          const formattedProducts = this.formatProductsForDisplay(response.data.products);
          const product = formattedProducts[0];
          
          // 既に追加済みの商品かチェック
          const existingProduct = this.allProducts.find(p => p.id === product.id);
          
          if (existingProduct) {
            alert('この商品は既に追加されています');
            return;
          }
          
          // 商品リストに追加
          this.allProducts.push(product);
          
          // 商品数を更新
          this.totalCount = this.allProducts.length;

          // 現在のページに表示する商品を設定
          this.updateCurrentPageProducts();
        } else if (response.data.success && response.data.products.length === 0) {
          alert('該当する商品が見つかりませんでした');
        } else {
          alert('商品検索に失敗しました');
        }
      } catch (error) {
        console.error('商品検索エラー:', error);
        alert('商品検索中にエラーが発生しました。');
      } finally {
        // 入力フィールドをクリア
        this.productCode = '';
        hideLoading();
      }
    },
    
    // 表示用にデータを整形するメソッド
    formatProductsForDisplay(products) {
      return products.map(product => {
        // 日付フォーマットの変換
        const formattedProduct = { ...product };
        
        // カテゴリIDをカテゴリ名に変換
        if (formattedProduct.categoryId) {
          formattedProduct.categoryName = this.getCategoryNameById(formattedProduct.categoryId);
        }
        
        // メーカーIDをメーカー名に変換
        if (formattedProduct.manufacturer) {
          formattedProduct.makerName = this.getMakerNameById(formattedProduct.manufacturer);
        }
        
        return formattedProduct;
      });
    },
    
    // カテゴリIDからカテゴリ名を取得
    getCategoryNameById(categoryId) {
      const category = this.categoryOptions.find(option => option.value === categoryId.toString());
      return category ? category.label : categoryId;
    },
    
    // メーカーIDからメーカー名を取得
    getMakerNameById(makerId) {
      const maker = this.manufacturerOptions.find(option => option.value === makerId.toString());
      return maker ? maker.label : makerId;
    },

    // 現在のページに表示する商品を更新
    updateCurrentPageProducts() {
      const startIndex = this.currentPage * this.perPage;
      const endIndex = startIndex + this.perPage;
      this.displayProductList = this.allProducts.slice(startIndex, endIndex);
    },
    
    // ページ変更ハンドラ
    onPageChange(page) {
      // ページ変更時にローカルページングを行う
      // FormListTableは1-indexedなので、0-indexedに変換する
      this.currentPage = page - 1;
      this.updateCurrentPageProducts();
    },
    
    // テーブルアクションのハンドラ
    handleTableAction(actionData) {
      const { action, item } = actionData;
      if (action === '削除') {
        this.deleteProduct(item);
      }
    },
    
    // 商品を削除
    deleteProduct(product) {
      const index = this.allProducts.findIndex(item => item.id === product.id);
      if (index === -1) return;
      this.allProducts.splice(index, 1);
      // 商品数を更新
      this.totalCount = this.allProducts.length;
      // 現在のページに表示する商品を設定
      this.updateCurrentPageProducts();
    },
    
    // 店舗移動出荷を登録
    async saveShipment() {
      if (this.allProducts.length === 0) {
        alert('商品が登録されていません');
        return;
      }
      
      try {
        showLoading();
        if (!confirm('出荷登録を行います。よろしいですか？')) {
          hideLoading();
          return;
        }
        
        // 店舗移動IDを取得
        const storeTransferId = this.$route.query.id;
        
        // 合計金額を計算（¥記号と区切り文字を除去して数値に変換）
        const totalAmount = this.calculateTotalAmountInt();
        
        // リクエストデータを作成
        const requestData = {
          storeTransferId: parseInt(storeTransferId),
          productIds: this.allProducts.map(product => product.id),
          totalAmount: totalAmount
        };
        
        // APIを呼び出して出荷登録
        const response = await service.registerShippingTransfer(requestData);
        
        if (response.data && response.data.isSuccess) {
          alert('出荷登録が完了しました。');
          // 店舗移動一覧画面に遷移
          this.$router.push({ name: 'StoreTransferList' });
        } else {
          throw new Error(response.data?.message || '出荷登録に失敗しました。');
        }
      } catch (error) {
        console.error('出荷登録エラー:', error);
        alert('出荷登録中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    // 店舗移動削除の確認ダイアログを表示
    confirmDeleteTransfer() {
      if (!this.storeTransfer) {
        alert('店舗移動データが読み込まれていません');
        return;
      }
      
      if (confirm('この店舗移動情報を削除してもよろしいですか？')) {
        this.deleteStoreTransfer();
      }
    },
    
    // 店舗移動を削除
    async deleteStoreTransfer() {
      try {
        showLoading();
        const storeTransferId = this.$route.query.id;
        
        if (!storeTransferId) {
          alert('店舗移動IDが指定されていません');
          return;
        }
        
        // 店舗移動削除APIを呼び出し
        const response = await service.deleteStoreTransfer(storeTransferId);
        
        if (response.data.isSuccess) {
          alert('店舗移動情報を削除しました');
          // 店舗移動一覧画面に遷移
          this.$router.push({ name: 'StoreTransferList' });
        } else {
          alert(response.data.message || '店舗移動情報の削除に失敗しました');
        }
      } catch (error) {
        console.error('店舗移動情報の削除中にエラーが発生しました:', error);
        alert('店舗移動情報の削除中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    calculateTotalAmountInt() {
      if (!this.allProducts || this.allProducts.length === 0) {
        return 0;
      }
      
      const total = this.allProducts.reduce((sum, product) => {
        const purchasePrice = product.purchasePrice || 0;
        const quantity = product.stock || 0;
        return sum + (purchasePrice * quantity);
      }, 0);
      
      return total;
    }
  }
}
</script>

<style scoped>
.footer-buttons {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 10px 0;
}

.footer-buttons button {
  margin-left: 15px;
}
</style>
